// src/pages/townships/TownshipsPage.tsx
import {useState, useMemo} from "react";
import {Eye, Edit, Trash2, Plus, MapPin} from "lucide-react";
import {useToast} from "../../hooks/common/useToast";
import {useConfirm} from "../../hooks/common/useConfirm";
import {useModal} from "../../hooks/common/useModal";
import type {
  CreateTownshipDto,
  TownshipResponseDto,
  UpdateTownshipDto,
} from "../../types";
import {useTable} from "../../hooks/common/useTable";

import {useRegions} from "../../hooks/useRegions";
import {Badge} from "../../components/ui";
import {PageHeader} from "../../components/common";
import {
  createCommonActions,
  createCommonBulkActions,
  DataTable,
} from "../../components/common/DataTable";
import type {
  DataTableAction,
  DataTableBulkAction,
  DataTableColumn,
} from "../../components/common/DataTable/DataTableTypes";

import FormModal from "../../components/common/Forms/FormModal";
import {useTownships} from "../../hooks/useTownship";

const validationRules = {
  required: {required: true},
};

const TownshipsPage: React.FC = () => {
  const {success, error} = useToast();
  const {confirm} = useConfirm();

  // Modals
  const createModal = useModal("create-township");
  const editModal = useModal("edit-township");

  // Selected township for editing
  const [selectedTownship, setSelectedTownship] =
    useState<TownshipResponseDto | null>(null);

  // Table state
  const {
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleFilterChange,
    getQueryParams,
    getSortDirection,
    isSorted,
    getFilterValue,
  } = useTable({
    key: "townships",
    defaultPageSize: 25,
  });

  // API hooks
  const {
    townships,
    pagination,
    stats,
    isLoading,
    handleCreate: createTownship,
    handleUpdate: updateTownship,
    handleDelete: deleteTownship,
    handleBulkCreate,
    refetch,
  } = useTownships(getQueryParams());

  const {dropdownRegions} = useRegions();

  // Get region options for forms
  const regionOptions = useMemo(
    () =>
      dropdownRegions.map((region) => ({
        value: region.id,
        label: region.nameEng,
      })),
    [dropdownRegions]
  );

  // Table columns
  const columns: DataTableColumn<TownshipResponseDto & Record<string, any>>[] =
    [
      {
        key: "nameEng",
        title: "Name (English)",
        sortable: true,
        render: (_, record) => (
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              {record.nameEng}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {record.nameMm}
            </div>
          </div>
        ),
      },
      {
        key: "region",
        title: "Region",
        render: (_, record) => (
          <div className="flex items-center space-x-2">
            <MapPin className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {record.region?.nameEng || "N/A"}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {record.region?.nameMm || ""}
              </div>
            </div>
          </div>
        ),
      },
      {
        key: "slug",
        title: "Slug",
        render: (value) => (
          <code className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded">
            {value as string}
          </code>
        ),
      },
      {
        key: "propertiesCount", // Changed from "propertyCount" to "propertiesCount"
        title: "Properties",
        align: "center",
        render: (value) => (
          <Badge variant="info">{(value as number) || 0}</Badge>
        ),
      },
      {
        key: "createdAt",
        title: "Created At",
        sortable: true,
        render: (value) => (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {new Date(value as string).toLocaleDateString()}
          </div>
        ),
      },
    ];

  // Table actions
  const commonActions = createCommonActions<
    TownshipResponseDto & Record<string, any>
  >();
  const actions: DataTableAction<TownshipResponseDto & Record<string, any>>[] =
    [
      commonActions.edit((record) => handleEdit(record)),
      commonActions.delete((record) => handleDeleteClick(record.id)),
    ];

  // Bulk actions
  const bulkActions: DataTableBulkAction<
    TownshipResponseDto & Record<string, any>
  >[] = [
    createCommonBulkActions<
      TownshipResponseDto & Record<string, any>
    >().bulkDelete((records) => handleBulkDelete(records.map((r) => r.id))),
  ];

  // Filters
  const filters = [
    {
      key: "search",
      label: "Search",
      type: "text" as const,
      placeholder: "Search townships...",
    },
    {
      key: "regionId",
      label: "Region",
      type: "select" as const,
      options: [{value: "", label: "All Regions"}, ...regionOptions],
    },
  ];

  // Form fields for create/edit
  const formFields = [
    {
      name: "nameEng",
      label: "Township Name (English)",
      type: "text" as const,
      required: true,
      placeholder: "Enter township name in English",
      validation: validationRules.required,
    },
    {
      name: "nameMm",
      label: "Township Name (Myanmar)",
      type: "text" as const,
      required: true,
      placeholder: "မြို့နယ်အမည် (မြန်မာ)",
      validation: validationRules.required,
    },
    {
      name: "regionId",
      label: "Region",
      type: "select" as const,
      required: true,
      options: [
        {value: "", label: "Select Region", disabled: true}, // Add placeholder option
        ...regionOptions,
      ],
      validation: validationRules.required,
    },
    {
      name: "slug",
      label: "URL Slug",
      type: "text" as const,
      placeholder: "Auto-generated from English name",
      helperText: "Leave empty to auto-generate from English name",
    },
  ];

  // Event handlers

  const handleCreateSubmit = async (values: Record<string, unknown>) => {
    const data: CreateTownshipDto = {
      nameEng: values.nameEng as string,
      nameMm: values.nameMm as string,
      regionId: values.regionId as number,
      slug: values.slug as string | "",
    };

    const success = await createTownship(data);
    if (success) {
      createModal.close();
      refetch();
    }
  };

  const handleEdit = (township: TownshipResponseDto) => {
    setSelectedTownship(township);
    editModal.open();
  };

  const handleUpdateSubmit = async (data: UpdateTownshipDto) => {
    if (!selectedTownship) return;

    const success = await updateTownship(selectedTownship.id, data);
    if (success) {
      editModal.close();
      setSelectedTownship(null);
      refetch();
    }
  };

  const handleDeleteClick = async (id: number) => {
    const success = await deleteTownship(id);
    if (success) {
      refetch();
    }
  };

  const handleBulkDelete = async (ids: number[]) => {
    const confirmed = await confirm({
      title: "Delete Townships",
      message: `Are you sure you want to delete ${ids.length} townships?`,
      variant: "danger",
      confirmText: "Delete",
    });

    if (!confirmed) return;

    try {
      // Note: You'll need to implement bulk delete in the hook
      success("Success", `${ids.length} townships deleted successfully`);
      refetch();
    } catch (err) {
      error("Error", "Failed to delete townships");
    }
  };

  const getEditInitialValues = () => {
    if (!selectedTownship) return {};

    return {
      nameEng: selectedTownship.nameEng,
      nameMm: selectedTownship.nameMm,
      regionId: selectedTownship.regionId,
      slug: selectedTownship.slug,
    };
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Townships"
        breadcrumbs={[
          {label: "Dashboard", path: "/dashboard"},
          {label: "Locations", path: "/regions"},
          {label: "Townships"},
        ]}
        action={{
          label: "Add Township",
          onClick: createModal.open,
          icon: <Plus className="w-4 h-4" />,
        }}
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <MapPin className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total Townships
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.totalTownships}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <MapPin className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Average per Region
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.round(stats.averageTownshipsPerRegion || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
                <MapPin className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Most Popular
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.mostPopularTownshipCount || 0}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  properties
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-dark-700">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                <MapPin className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Without Properties
                </p>
                {/* <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.townshipsWithoutProperties || 0}
                </p> */}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <DataTable
        data={townships as (TownshipResponseDto & Record<string, any>)[]}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        // Pagination
        pagination={{
          current: currentPage,
          pageSize,
          total: pagination?.total || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: true,
          onChange: handlePageChange,
        }}
        // Sorting
        sortable
        onSort={(sort) => {
          if (sort) {
            handleSort(sort.field);
          }
        }}
        // Filtering
        filters={filters}
        onFilter={(filters) => {
          Object.entries(filters).forEach(([key, value]) => {
            handleFilterChange(key, value);
          });
        }}
        // Search
        searchable
        searchPlaceholder="Search townships..."
        onSearch={(value) => handleFilterChange("search", value)}
        // Actions
        actions={actions}
        bulkActions={bulkActions}
        // Empty state
        emptyText="No townships found"
        emptyIcon={<MapPin className="w-12 h-12" />}
      />

      {/* Create Modal */}
      <FormModal
        isOpen={createModal.isOpen}
        onClose={createModal.close}
        title="Add New Township"
        fields={formFields}
        onSubmit={handleCreateSubmit}
        submitText="Create Township"
      />

      {/* Edit Modal */}
      <FormModal
        isOpen={editModal.isOpen}
        onClose={() => {
          editModal.close();
          setSelectedTownship(null);
        }}
        title="Edit Township"
        fields={formFields}
        initialValues={getEditInitialValues()}
        onSubmit={handleUpdateSubmit}
        submitText="Update Township"
      />
    </div>
  );
};

export default TownshipsPage;
